import { arrow, autoUpdate, flip, offset, shift, size, useFloating, type Placement } from "@floating-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight, faForward, faTimes } from "@fortawesome/free-solid-svg-icons";
import type { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { FOCUS_RING } from "@/components/ui/styles";

type HintBalloonBaseProps = {
  /** localStorage key — a completed guide is not shown again. */
  storageKey: string;
  /** Preferred side of the anchor. Collision handling can flip this side. */
  tail?: "top" | "bottom";
  /** Preferred horizontal alignment against the anchor. */
  align?: "start" | "center" | "end";
  /** @deprecated Arrow placement follows the final floating position. */
  tailAlign?: "start" | "center" | "end";
  /** External visibility gate (for example, loading guards). Defaults to true. */
  visible?: boolean;
  /** Complete this guide when an interactive wrapped child is activated. */
  completeOnAnchorAction?: boolean;
  children?: ReactNode;
  className?: string;
  /** @deprecated Called when this guide, rather than every hint on the screen, is completed. */
  onDismissAll?: () => void;
};

type HintStep = {
  id: string;
  title?: ReactNode;
  message: ReactNode;
  icon?: IconDefinition;
};

type SingleHintBalloonProps = HintBalloonBaseProps & {
  mode?: "single";
  title?: ReactNode;
  message: ReactNode;
  icon?: IconDefinition;
  steps?: never;
  initialStepId?: never;
  onStepChange?: never;
};

type StepHintBalloonProps = HintBalloonBaseProps & {
  mode: "steps";
  steps: readonly HintStep[];
  initialStepId?: string;
  onStepChange?: (stepId: string, index: number) => void;
  title?: never;
  message?: never;
  icon?: never;
};

export type HintBalloonProps = SingleHintBalloonProps | StepHintBalloonProps;

type PersistedHintState = { dismissed: boolean; stepId?: string };
type HintRegistration = {
  id: string;
  storageKey: string;
  element: HTMLElement | null;
  eligible: boolean;
  onComplete: () => void;
};
type HintBalloonCoordinator = {
  activeId: string | null;
  completedKeys: ReadonlySet<string>;
  resetVersion: number;
  register: (registration: HintRegistration) => void;
  unregister: (id: string) => void;
  complete: (id: string) => void;
};

const STORAGE_VERSION = "v2";
const STORAGE_KEY_PREFIX = "hint-balloon";
const RESET_EVENT = "hint-balloon:reset";
const HintBalloonContext = createContext<HintBalloonCoordinator | null>(null);

function getHintStorageKey(storageKey: string) {
  return `${STORAGE_KEY_PREFIX}:${storageKey}:${STORAGE_VERSION}`;
}

function readHintState(storageKey: string): PersistedHintState {
  if (typeof window === "undefined") return { dismissed: false };

  try {
    const raw = window.localStorage.getItem(getHintStorageKey(storageKey));
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<PersistedHintState>;
      return { dismissed: parsed.dismissed === true, stepId: typeof parsed.stepId === "string" ? parsed.stepId : undefined };
    }
  } catch (error) {
    console.warn("HintBalloon: failed to parse persisted hint state", error);
  }

  return { dismissed: window.localStorage.getItem(storageKey) === "true" };
}

function writeHintState(storageKey: string, value: PersistedHintState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(getHintStorageKey(storageKey), JSON.stringify(value));
    if (value.dismissed) window.localStorage.setItem(storageKey, "true");
    else window.localStorage.removeItem(storageKey);
  } catch (error) {
    console.warn("HintBalloon: failed to persist hint state", error);
  }
}

/** Migrates a dismissed per-item guide into its replacement shared guide. */
export function migrateHintBalloonDismissal(storageKey: string, legacyStorageKeyPrefix: string) {
  if (typeof window === "undefined" || readHintState(storageKey).dismissed) return;
  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index);
      if (!key) continue;
      const isLegacyKey = key.startsWith(legacyStorageKeyPrefix);
      const isVersionedLegacyKey = key.startsWith(`${STORAGE_KEY_PREFIX}:${legacyStorageKeyPrefix}`) && key.endsWith(`:${STORAGE_VERSION}`);
      if (!isLegacyKey && !isVersionedLegacyKey) continue;
      const raw = window.localStorage.getItem(key);
      let dismissed = raw === "true";
      if (!dismissed) {
        try {
          dismissed = JSON.parse(raw ?? "{}")?.dismissed === true;
        } catch {
          dismissed = false;
        }
      }
      if (dismissed) {
        writeHintState(storageKey, { dismissed: true });
        return;
      }
    }
  } catch (error) {
    console.warn("HintBalloon: failed to migrate persisted hint state", error);
  }
}

export function resetHintBalloon(storageKey: string) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(storageKey);
    window.localStorage.removeItem(getHintStorageKey(storageKey));
    window.dispatchEvent(new CustomEvent<string>(RESET_EVENT, { detail: storageKey }));
  } catch (error) {
    console.warn("HintBalloon: failed to reset hint state", error);
  }
}

function compareRegistrations(a: HintRegistration, b: HintRegistration) {
  if (a.element && b.element && a.element !== b.element) {
    const position = a.element.compareDocumentPosition(b.element);
    if (position & Node.DOCUMENT_POSITION_FOLLOWING) return -1;
    if (position & Node.DOCUMENT_POSITION_PRECEDING) return 1;
  }
  return a.id.localeCompare(b.id);
}

/** Coordinates one active guide per route while preserving each guide's persistence. */
export function HintBalloonProvider({ screenKey, children }: { screenKey: string; children: ReactNode }) {
  const registrationsRef = useRef(new Map<string, HintRegistration>());
  const [registryVersion, setRegistryVersion] = useState(0);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [completedKeys, setCompletedKeys] = useState<ReadonlySet<string>>(() => new Set());
  const [resetVersion, setResetVersion] = useState(0);

  const register = useCallback((registration: HintRegistration) => {
    registrationsRef.current.set(registration.id, registration);
    setRegistryVersion((version) => version + 1);
  }, []);
  const unregister = useCallback((id: string) => {
    if (registrationsRef.current.delete(id)) setRegistryVersion((version) => version + 1);
  }, []);
  const complete = useCallback((id: string) => {
    const registration = registrationsRef.current.get(id);
    if (!registration) return;
    setCompletedKeys((keys) => new Set([...keys, registration.storageKey]));
    setActiveId(null);
    registration.onComplete();
  }, []);

  useEffect(() => {
    const handleReset = (event: Event) => {
      const storageKey = (event as CustomEvent<string>).detail;
      setCompletedKeys((keys) => {
        if (!keys.has(storageKey)) return keys;
        const next = new Set(keys);
        next.delete(storageKey);
        return next;
      });
      setResetVersion((version) => version + 1);
    };
    window.addEventListener(RESET_EVENT, handleReset);
    return () => window.removeEventListener(RESET_EVENT, handleReset);
  }, []);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const firstRegistrationForKey = new Map<string, HintRegistration>();
      const candidates = [...registrationsRef.current.values()]
        .filter((registration) => registration.eligible && registration.element?.isConnected && !completedKeys.has(registration.storageKey))
        .sort(compareRegistrations);
      for (const candidate of candidates) {
        if (!firstRegistrationForKey.has(candidate.storageKey)) firstRegistrationForKey.set(candidate.storageKey, candidate);
      }
      const queue = [...firstRegistrationForKey.values()];
      setActiveId((current) => (current && queue.some((candidate) => candidate.id === current) ? current : queue[0]?.id ?? null));
    });
    return () => window.cancelAnimationFrame(frame);
  }, [completedKeys, registryVersion, resetVersion, screenKey]);

  const value = useMemo<HintBalloonCoordinator>(
    () => ({ activeId, completedKeys, resetVersion, register, unregister, complete }),
    [activeId, completedKeys, complete, register, resetVersion, unregister],
  );
  return <HintBalloonContext.Provider value={value}>{children}</HintBalloonContext.Provider>;
}

function useHintBalloonCoordinator() {
  const coordinator = useContext(HintBalloonContext);
  if (!coordinator) throw new Error("HintBalloon must be rendered inside HintBalloonProvider");
  return coordinator;
}

function useMobileViewport() {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 639px)");
    const update = () => setIsMobile(mediaQuery.matches);
    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);
  return isMobile;
}

function isInteractiveAction(target: EventTarget | null) {
  return target instanceof Element && Boolean(target.closest("button, a, input, select, textarea, [role='button']"));
}

function getPlacement(tail: "top" | "bottom", align: "start" | "center" | "end"): Placement {
  const side = tail === "top" ? "bottom" : "top";
  return (align === "center" ? side : `${side}-${align}`) as Placement;
}

function isOutsideViewport(element: HTMLElement) {
  const rect = element.getBoundingClientRect();
  return rect.bottom < 16 || rect.top > window.innerHeight - 16 || rect.right < 16 || rect.left > window.innerWidth - 16;
}

const ANIMATION_STYLE = `
  @keyframes _hb_in { from { opacity: 0; transform: translateY(var(--hb-enter-y, -6px)) scale(0.98); } to { opacity: 1; transform: translateY(0) scale(1); } }
  ._hb-content { animation: _hb_in 0.2s cubic-bezier(0.16, 1, 0.3, 1) both; }
  @media (prefers-reduced-motion: reduce) { ._hb-content { animation: none; } }
`;

export function HintBalloon({
  storageKey,
  mode = "single",
  tail = "top",
  align = "start",
  visible = true,
  completeOnAnchorAction = true,
  children,
  className = "",
  onDismissAll,
  steps,
  initialStepId,
  onStepChange,
  title,
  message,
  icon,
}: HintBalloonProps) {
  const id = useId();
  const coordinator = useHintBalloonCoordinator();
  const { activeId, completedKeys, complete, register, resetVersion, unregister } = coordinator;
  const [anchorElement, setAnchorElement] = useState<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);
  const onDismissRef = useRef(onDismissAll);
  const [hintState, setHintState] = useState<PersistedHintState>(() => readHintState(storageKey));
  const [anchorReady, setAnchorReady] = useState(false);
  const isMobile = useMobileViewport();
  useEffect(() => {
    onDismissRef.current = onDismissAll;
  }, [onDismissAll]);

  useEffect(() => setHintState(readHintState(storageKey)), [resetVersion, storageKey]);
  const initialStepIndex = useMemo(() => {
    if (mode !== "steps" || !steps?.length) return 0;
    const persistedIndex = hintState.stepId ? steps.findIndex((step) => step.id === hintState.stepId) : -1;
    if (persistedIndex >= 0) return persistedIndex;
    const preferredIndex = initialStepId ? steps.findIndex((step) => step.id === initialStepId) : -1;
    return preferredIndex >= 0 ? preferredIndex : 0;
  }, [hintState.stepId, initialStepId, mode, steps]);
  const [stepIndex, setStepIndex] = useState(initialStepIndex);
  useEffect(() => setStepIndex(initialStepIndex), [initialStepIndex, storageKey]);
  useEffect(() => {
    if (mode !== "steps" || !steps?.length) return;
    const clampedIndex = Math.max(0, Math.min(stepIndex, steps.length - 1));
    const step = steps[clampedIndex];
    if (step) onStepChange?.(step.id, clampedIndex);
  }, [mode, onStepChange, stepIndex, steps]);

  const eligible = !hintState.dismissed && !completedKeys.has(storageKey) && visible && (mode === "single" || Boolean(steps?.length));
  const isActive = activeId === id && eligible;
  const finish = useCallback(() => {
    const nextState = { dismissed: true, stepId: hintState.stepId };
    setHintState(nextState);
    writeHintState(storageKey, nextState);
    complete(id);
  }, [complete, hintState.stepId, id, storageKey]);

  useLayoutEffect(() => {
    register({ id, storageKey, element: anchorElement, eligible, onComplete: () => onDismissRef.current?.() });
    return () => unregister(id);
  }, [anchorElement, eligible, id, register, storageKey, unregister]);

  useEffect(() => {
    if (!isActive || !anchorElement) {
      setAnchorReady(false);
      return;
    }
    if (isOutsideViewport(anchorElement)) {
      const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      anchorElement.scrollIntoView({ behavior: reducedMotion ? "auto" : "smooth", block: "center", inline: "nearest" });
    }
    const frame = window.requestAnimationFrame(() => setAnchorReady(true));
    return () => window.cancelAnimationFrame(frame);
  }, [anchorElement, isActive]);
  useEffect(() => {
    if (!isActive) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        finish();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [finish, isActive]);

  const viewportPadding = isMobile ? 88 : 16;
  const { refs, floatingStyles, placement, middlewareData } = useFloating({
    open: isActive && anchorReady,
    placement: getPlacement(tail, align),
    strategy: "fixed",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(10),
      flip({ fallbackPlacements: [tail === "top" ? "top" : "bottom"], padding: viewportPadding }),
      shift({ padding: viewportPadding }),
      size({ padding: viewportPadding, apply({ availableWidth, availableHeight, elements }) { Object.assign(elements.floating.style, { maxWidth: `${Math.max(0, availableWidth)}px`, maxHeight: `${Math.max(0, availableHeight)}px` }); } }),
      arrow({ element: arrowRef, padding: 16 }),
    ],
  });
  const setAnchor = useCallback((node: HTMLDivElement | null) => {
    setAnchorElement(node);
    refs.setReference(node);
  }, [refs]);

  function goToStep(index: number) {
    if (mode !== "steps" || !steps?.length) return;
    const nextIndex = Math.max(0, Math.min(index, steps.length - 1));
    const nextStep = steps[nextIndex];
    if (!nextStep) return;
    setStepIndex(nextIndex);
    const nextState = { dismissed: false, stepId: nextStep.id };
    setHintState(nextState);
    writeHintState(storageKey, nextState);
  }

  const activeStep = mode === "steps" && steps ? steps[Math.min(stepIndex, steps.length - 1)] : null;
  const displayTitle = mode === "steps" ? activeStep?.title : title;
  const displayMessage = mode === "steps" ? activeStep?.message : message;
  const displayIcon = mode === "steps" ? activeStep?.icon : icon;
  const isLastStep = mode !== "steps" || !steps || stepIndex === steps.length - 1;
  const hasPreviousStep = mode === "steps" && stepIndex > 0;
  const stepLabel = mode === "steps" && steps ? `Step ${stepIndex + 1} of ${steps.length}` : null;
  const actualSide = placement.split("-")[0];
  const arrowX = middlewareData.arrow?.x;
  const fallbackArrowPosition = align === "start" ? "20px" : align === "end" ? "calc(100% - 20px)" : "50%";
  const arrowPosition = { left: arrowX == null ? fallbackArrowPosition : `${arrowX}px` };
  const arrowSideClass = actualSide === "bottom" ? "-top-[8px]" : "-bottom-[8px]";
  const arrowBorderClass = actualSide === "bottom" ? "border-b-[8px] border-b-primary_button/35" : "border-t-[8px] border-t-primary_button/35";
  const arrowFillClass = actualSide === "bottom" ? "-top-[5px] border-b-[6px] border-b-light-background dark:border-b-dark-nestedbg" : "-bottom-[5px] border-t-[6px] border-t-light-background dark:border-t-dark-nestedbg";
  const titleId = `${id}-title`;
  const descriptionId = `${id}-description`;

  const balloon = isActive && anchorReady && typeof document !== "undefined" ? createPortal(
    <div ref={refs.setFloating} style={floatingStyles} className="z-[60] w-[min(300px,calc(100vw-2rem))] overflow-visible">
      <style>{ANIMATION_STYLE}</style>
      <div data-hint-balloon-panel role="dialog" aria-modal="false" aria-labelledby={displayTitle ? titleId : undefined} aria-describedby={descriptionId} aria-live="polite" className="_hb-content relative overflow-visible rounded-xl border border-primary_button/35 bg-light-background p-3.5 shadow-[0_8px_32px_-4px_rgba(0,0,0,0.18),0_2px_8px_-2px_rgba(0,0,0,0.08)] dark:bg-dark-nestedbg dark:shadow-[0_8px_32px_-4px_rgba(0,0,0,0.55),0_2px_8px_-2px_rgba(0,0,0,0.35)]" style={{ "--hb-enter-y": actualSide === "bottom" ? "-6px" : "6px" } as CSSProperties}>
        <div ref={arrowRef} aria-hidden className={`absolute -translate-x-1/2 border-l-[7px] border-r-[7px] border-l-transparent border-r-transparent ${arrowSideClass} ${arrowBorderClass}`} style={arrowPosition} />
        <div aria-hidden className={`absolute -translate-x-1/2 border-l-[6px] border-r-[6px] border-l-transparent border-r-transparent ${arrowFillClass}`} style={arrowPosition} />
        <div className="flex min-w-0 items-start gap-2.5">
          {displayIcon && <FontAwesomeIcon icon={displayIcon} className="mt-1 shrink-0 text-xs text-primary_button" />}
          <div className="min-w-0 flex-1">
            {stepLabel && <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-primary_button/80">{stepLabel}</p>}
            {displayTitle && <p id={titleId} className="mb-1 text-xs font-semibold text-light/90 dark:text-dark/90">{displayTitle}</p>}
            <p id={descriptionId} className="break-words text-xs leading-relaxed text-light/60 dark:text-dark/60">{displayMessage}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-light-border/40 pt-2 dark:border-dark-border/40">
          <div>{hasPreviousStep && <button type="button" onClick={() => goToStep(stepIndex - 1)} className={`inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-xs font-semibold text-light/70 transition-colors hover:text-light/95 dark:text-dark/70 dark:hover:text-dark/95 ${FOCUS_RING}`}><FontAwesomeIcon icon={faArrowLeft} className="text-[10px]" />Previous</button>}</div>
          <div className="flex items-center gap-1">
            <button type="button" onClick={finish} aria-label="Skip this guide" className={`inline-flex min-h-9 items-center gap-1 rounded-md px-2 text-xs font-semibold text-light/60 transition-colors hover:text-light/90 dark:text-dark/60 dark:hover:text-dark/90 ${FOCUS_RING}`}><FontAwesomeIcon icon={faTimes} className="text-[10px]" />Skip</button>
            <button type="button" onClick={() => (isLastStep ? finish() : goToStep(stepIndex + 1))} className={`inline-flex min-h-9 items-center gap-1 rounded-md px-3 text-xs font-semibold text-primary_button transition-colors hover:text-primary_button/75 ${FOCUS_RING}`}>{isLastStep ? "Done" : "Next"}<FontAwesomeIcon icon={isLastStep ? faForward : faArrowRight} className="text-[10px]" /></button>
          </div>
        </div>
      </div>
    </div>, document.body,
  ) : null;

  return <div ref={setAnchor} className={children ? `relative ${className}` : `relative h-0 overflow-visible ${className}`} onClickCapture={children && completeOnAnchorAction ? (event) => {
    const target = event.target;
    if (target instanceof Element && target.closest("[data-hint-balloon-panel]")) return;
    if (isActive && isInteractiveAction(target)) finish();
  } : undefined}>{children}{balloon}</div>;
}
